import { Overlay } from "../overlay/overlay.tsx";
import { useMergedClassName } from "../utils.ts";
import { useEffect, useMemo, useState } from "preact/hooks";
import type { ComponentChildren, FunctionComponent, HTMLAttributes } from "preact";

export type DrawerProps = HTMLAttributes<HTMLDivElement> & {
    /** open status */
    isOpen?: boolean;
    /** callback when closed by the inner component, usually () => setIsOpen(false) */
    onClose?: () => void;
    children?: ComponentChildren;
};

const CLASS_NAME_OPENING = [
    "tinyui_drawer_bg_shadow animate__animated animate__fadeIn animate__faster",
    "tinyui_drawer_container animate__animated animate__slideInDown animate__faster",
];

const CLASS_NAME_CLOSING = [
    "tinyui_drawer_bg_shadow animate__animated animate__fadeOut animate__faster",
    "tinyui_drawer_container animate__animated animate__slideOutUp animate__faster",
];

export const Drawer: FunctionComponent<DrawerProps> = (props: DrawerProps) => {
    // Optimization: Track "visible" state which includes closing phase
    const [isVisible, setIsVisible] = useState(props.isOpen ?? false);
    
    // Sync visible state immediately when opening to avoid frame delay
    if (props.isOpen && !isVisible) {
        setIsVisible(true);
    }

    const [styleClassNames, setStyleClassNames] = useState(props.isOpen ? CLASS_NAME_OPENING : CLASS_NAME_CLOSING);
    
    // Sync class names immediately when opening
    if (props.isOpen && styleClassNames === CLASS_NAME_CLOSING) {
        setStyleClassNames(CLASS_NAME_OPENING);
    }

    const classNameContent = useMergedClassName(styleClassNames[1], props.class);

    useEffect(() => {
        if (props.isOpen) {
            // Already handled by render-time state update for speed
            // Just ensure cleanup if needed
            return () => {};
        } else {
            // Start closing animation
            setStyleClassNames(CLASS_NAME_CLOSING);
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 300); // Reduce to 300ms for snappier feel
            return () => {
                clearTimeout(timer);
            };
        }
    }, [props.isOpen]);

    const onClose = useMemo(() => props.onClose ? props.onClose : () => {}, [props.onClose]);

    return (
        <Overlay isOpen={isVisible}>
            <div class={styleClassNames[0]}></div>
            <div class="tinyui_drawer_wrapper" onClick={onClose}>
                <div
                    {...props}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    class={classNameContent}
                >
                    {props.children}
                </div>
            </div>
        </Overlay>
    );
};
